import { useCallback, useMemo } from 'react'

import { TrashBin } from '@gravity-ui/icons'
import { Button, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { VehicleAutocomplete } from '@/components/common/VehicleAutocomplete'
import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { useVehiclesStore } from '@/store/vehicles.store'
import { useWarehouseStore } from '@/store/warehouse.store'

interface VehicleReportsTabProps {
  onDelete: (id: string) => void;
}

export function VehicleReportsTab({ onDelete }: VehicleReportsTabProps) {
  const { t } = useTranslation()
  const { vehicles } = useVehiclesStore()
  const {
    vehicleReport,
    isReportLoading,
    reportVehicleId,
    reportVehiclePlate,
    setReportVehicle,
    reportPagination,
    setReportPagination,
  } = useWarehouseStore()

  const reportData = vehicleReport

  // Calculate Report summary metrics
  const reportSummary = useMemo(() => {
    let totalCount = 0
    let totalValue = 0
    reportData.forEach(item => {
      totalCount += item.count
      totalValue += item.totalCost
    })
    return { totalCount, totalValue }
  }, [reportData])

  const initialReportVehicle = useMemo(() => {
    if (reportVehicleId !== 'none') {
      const v = vehicles.find(v => v.id === reportVehicleId)
      if (v) {
        return {
          ID: v.id,
          Number: v.plate,
          Model: v.brand,
          Make: v.year,
        }
      }
    }
    return null
  }, [reportVehicleId, vehicles])

  const handlePageChange = useCallback((pageNumber: number) => {
    setReportPagination({ page: pageNumber })
  }, [setReportPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setReportPagination({ limit, page: 0 })
  }, [setReportPagination])

  const start = (reportPagination.page == 0 ? 0 : reportPagination.page) * reportPagination.limit + 1
  const end = Math.min((reportPagination.page + 1) * reportPagination.limit, reportPagination.total)

  return (
    <div className="space-y-5">
      {/* Filter panel */}
      <div className="max-w-xs">
        <VehicleAutocomplete
          placeholder={t('warehouse.reports.vehicle_placeholder')}
          selectedId={reportVehicleId}
          initialValue={initialReportVehicle}
          onSelectionChange={vehicle => {
            if (vehicle) {
              setReportVehicle(vehicle.ID, vehicle.Number)
            } else {
              setReportVehicle('none', null)
            }
          }}
        />
      </div>

      {reportVehiclePlate ? (
        isReportLoading ? (
          <div className="flex justify-center py-10">
            <svg className="animate-spin h-8 w-8 text-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : reportData.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 border border-dashed rounded-xl border-zinc-200 dark:border-zinc-800">
            {t('warehouse.reports.no_data')}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary card metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-default-50 border border-default-100 rounded-xl">
                <span className="text-xs text-zinc-500">{t('warehouse.reports.parts_installed')}</span>
                <span className="text-2xl font-bold block mt-1">{reportSummary.totalCount} шт</span>
              </div>
              <div className="p-4 bg-default-50 border border-default-100 rounded-xl">
                <span className="text-xs text-zinc-500">{t('warehouse.reports.total_valuation')}</span>
                <span className="text-2xl font-bold block mt-1">{reportSummary.totalValue.toFixed(2).replace('.', ',')} BYN</span>
              </div>
            </div>

            {/* Report table */}
            <Table className="min-h-[200px]">
              <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.96))]">
                <Table.Content aria-label={t('warehouse.reports.table_label')}>
                  <Table.Header>
                    <Table.Column id="part_name">{t('warehouse.article_name')}</Table.Column>
                    <Table.Column id="install_count">{t('warehouse.count')}</Table.Column>
                    <Table.Column id="date">{t('warehouse.date')}</Table.Column>
                    <Table.Column id="total_cost">{t('warehouse.total_cost')}</Table.Column>
                    <Table.Column id="actions"></Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {reportData.map(item => (
                      <Table.Row key={item.id} className="hover:bg-default-50 cursor-default">
                        <Table.Cell>
                          <span className="font-bold text-foreground">{item.name}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span>{item.count}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="font-mono text-zinc-600 dark:text-zinc-400">{item.date}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="font-semibold">{item.totalCost.toFixed(2).replace('.', ',')} BYN</span>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex justify-end gap-2">
                            <Tooltip delay={0}>
                              <Button
                                isIconOnly
                                size="sm"
                                variant="danger-soft"
                                onClick={e => {
                                  e.stopPropagation()
                                  onDelete(item.id)
                                }}
                              >
                                <TrashBin className="size-4" />
                              </Button>
                              <Tooltip.Content>
                                <p>{t('common.delete')}</p>
                              </Tooltip.Content>
                            </Tooltip>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>

              <Table.Footer>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-4">
                    <RowsPerPage
                      value={reportPagination.limit}
                      onChange={handleLimitChange}
                    />
                    <span className="text-xs text-foreground/50">
                      {t('common.pagination_summary', { start, end, total: reportPagination.total })}
                    </span>
                  </div>
                  <Pagination
                    page={reportPagination.page}
                    total={reportPagination.totalPages}
                    onChange={handlePageChange}
                  />
                </div>
              </Table.Footer>
            </Table>
          </div>
        )
      ) : (
        <div className="text-center py-10 text-zinc-500 border border-dashed rounded-xl border-zinc-200 dark:border-zinc-800">
          {t('warehouse.reports.prompt_select_vehicle')}
        </div>
      )}
    </div>
  )
}
